import { FullDatabaseRevisionSchema } from 'libs/end-to-end/src/types/databases/revisions/database-revision-schema';
import { DatabaseRevisionSchemaValidationByMetricTrendsError } from 'libs/end-to-end/src/types/databases/revisions/database-revision-schema-validation-result';
import { DatabaseRevisionSchemaValidationCopilotAutoMLConfigurationError } from 'libs/end-to-end/src/types/databases/revisions/database-revision-schema-validation-result';
import { DatabaseRevisionSchemaValidationCopilotError } from 'libs/end-to-end/src/types/databases/revisions/database-revision-schema-validation-result';
import { DatabaseRevisionSchemaValidationMiscError } from 'libs/end-to-end/src/types/databases/revisions/database-revision-schema-validation-result';
import { DatabaseRevisionSchemaValidationUpsellError } from 'libs/end-to-end/src/types/databases/revisions/database-revision-schema-validation-result';
import { DatabaseRevisionSchemaValidationDimensionInsights } from 'libs/end-to-end/src/types/databases/revisions/database-revision-schema-validation-result';
import { DatabaseRevisionSchemaValidationFocusAreaError } from 'libs/end-to-end/src/types/databases/revisions/database-revision-schema-validation-result';
import { DatabaseRevisionSchemaValidationResult } from 'libs/end-to-end/src/types/databases/revisions/database-revision-schema-validation-result';
import { DimensionDefinition } from 'libs/end-to-end/src/types/databases/dimensions/dimension-definition';
import { TransformInsights } from 'libs/end-to-end/src/types/databases/transform/transform-insights';
import { TransformPossibleDimension } from 'libs/end-to-end/src/types/databases/transform/transform-insights';
import { generateDimensionLevels } from 'libs/end-to-end/src/types/databases/dimensions/generate-dimensions';
import { FocusAreaDefinition } from 'libs/end-to-end/src/types/databases/focus-areas/focus-area-definition';
import { FullCopilotDefinition } from 'libs/end-to-end/src/types/databases/copilots/copilot-definition';
import { DataSetReference } from 'libs/end-to-end/src/types/databases/data-sets/data-set-reference';
import { DefaultCalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { isValidOverrideUploadSnapshotDate } from 'libs/end-to-end/src/utils/databases/isValidOverrideUploadSnapshotDate';
import { getAllFilterSetsAsFilterSetDefinitions } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { validateFilterSets } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { validateDatabaseSchemaMetricDefinition } from 'libs/end-to-end/src/utils/databases/validateDatabaseSchemaMetricDefinition';
import { HiringNotificationEmailSubjectSuffixTemplate } from 'libs/end-to-end/src/types/hiring-notification-sender/hiring-notification-email-subject-suffix-template';
import { CopilotAutoMLConfiguration } from 'libs/end-to-end/src/copilot';
import { LevelDefinition } from 'libs/end-to-end/src/types/databases/dimensions/level-definition';

const isRestrictedColumn = (columnName: string): boolean => {
  const lowerColumn = columnName.toLowerCase();
  return lowerColumn === 'location' || lowerColumn === 'department';
};

export const filterRestrictedColumns = (columns: string[]): string[] => {
  return columns.filter((column) => !isRestrictedColumn(column));
};

export const filterRestrictedDimensions = (
  dimensions: TransformPossibleDimension[],
): TransformPossibleDimension[] => {
  return dimensions.filter((dimension) => !isRestrictedColumn(dimension.name));
};

export const validateCopilotAutoMLConfiguration = (
  config: CopilotAutoMLConfiguration,
): DatabaseRevisionSchemaValidationCopilotAutoMLConfigurationError[] => {
  const copilotAutoMLConfigurationErrors: DatabaseRevisionSchemaValidationCopilotAutoMLConfigurationError[] =
    [];

  if (config.preset === 'custom') {
    if (
      config.featureSelection.naColumnFilter < 0 ||
      config.featureSelection.naColumnFilter > 1
    ) {
      copilotAutoMLConfigurationErrors.push({
        reason: 'na-column-filter-invalid',
        validationMessage:
          'NA Column Filter must be a number between 0.0 and 1.0',
      });
    }

    if (
      config.featureSelection.selectedTopPercentile < 0 ||
      config.featureSelection.selectedTopPercentile > 1
    ) {
      copilotAutoMLConfigurationErrors.push({
        reason: 'selected-top-percentile-invalid',
        validationMessage:
          'Selected Top Percentile must be a number between 0.0 and 1.0',
      });
    }

    if (config.autoMlSpecifics.huberLossDelta < 0) {
      copilotAutoMLConfigurationErrors.push({
        reason: 'huber-loss-delta-invalid',
        validationMessage:
          'Huber Loss Delta must be greater than or equal to 0',
      });
    }

    if (
      config.autoMlSpecifics.robustnessPenalty < 0 ||
      config.autoMlSpecifics.robustnessPenalty > 1
    ) {
      copilotAutoMLConfigurationErrors.push({
        reason: 'robustness-penalty-invalid',
        validationMessage:
          'Robustness Penalty must be a number between 0.0 and 1.0',
      });
    }
  }

  return copilotAutoMLConfigurationErrors;
};

export const validateDatabaseSchema = ({
  currentSchema,
  possibleDimensions,
  possibleMetrics,
  possibleControlVariables,
  dateColumns,
  numericColumns,
  uniqueValuesByColumn,
}: {
  currentSchema: FullDatabaseRevisionSchema;
  possibleDimensions: TransformPossibleDimension[];
  possibleMetrics: string[];
  possibleControlVariables: string[];
  uniqueValuesByColumn: TransformInsights['uniqueValuesByColumn'];
  numericColumns: TransformInsights['numericColumns'];
  dateColumns: TransformInsights['dateColumns'];
}): DatabaseRevisionSchemaValidationResult => {
  const insights: DatabaseRevisionSchemaValidationDimensionInsights[] = [];
  const newSchemaDimensions: DimensionDefinition[] = [];
  const filterSets = getAllFilterSetsAsFilterSetDefinitions({
    defaultFilters: currentSchema.filters,
    filterSets: currentSchema.filterSets ?? [],
  });
  const newSchemaFocusAreas: FocusAreaDefinition[] = [];
  const newSchemaCopilots: FullCopilotDefinition[] = [];

  const focusAreaErrors: DatabaseRevisionSchemaValidationFocusAreaError[] = [];
  const filterErrors = validateFilterSets({
    filterSets,
    numericColumns,
    dateColumns,
    uniqueValuesByColumn,
  });
  const copilotErrors: DatabaseRevisionSchemaValidationCopilotError[] = [];
  const copilotAutoMLConfigurationErrors =
    currentSchema.copilotAutoMLConfiguration
      ? validateCopilotAutoMLConfiguration(
          currentSchema.copilotAutoMLConfiguration,
        )
      : [];
  const upsellErrors: DatabaseRevisionSchemaValidationUpsellError[] = [];
  const miscErrors: DatabaseRevisionSchemaValidationMiscError[] = [];

  const possibleDimensionColumnHeaders = possibleDimensions.map(
    (dimension) => dimension.name,
  );

  for (const dimension of currentSchema.dimensions) {
    if (!possibleDimensionColumnHeaders.includes(dimension.columnHeaderValue)) {
      insights.push({
        columnHeaderValue: dimension.columnHeaderValue,
        didChangeLevels: false,
        isInvalid: true,
        validationMessage: 'Dimension is not valid',
      });
      newSchemaDimensions.push(dimension);
    } else if (isRestrictedColumn(dimension.columnHeaderValue)) {
      insights.push({
        columnHeaderValue: dimension.columnHeaderValue,
        didChangeLevels: false,
        isInvalid: true,
        validationMessage: 'Dimension is restricted',
      });
      newSchemaDimensions.push(dimension);
    } else {
      const findPossibleDimension = possibleDimensions.find(
        (possibleDimension) =>
          possibleDimension.name === dimension.columnHeaderValue,
      );
      if (findPossibleDimension) {
        const levelsInPossibleDimension = findPossibleDimension.values;

        const privilegedLevels = dimension.levels.filter(
          (level) => level.isPrivileged,
        );
        if (privilegedLevels.length !== 1) {
          insights.push({
            columnHeaderValue: dimension.columnHeaderValue,
            didChangeLevels: false,
            isInvalid: true,
            validationMessage: 'There must be exactly one privileged level',
          });
          newSchemaDimensions.push(dimension);
          continue;
        }

        if (dimension.levels.length < 2) {
          insights.push({
            columnHeaderValue: dimension.columnHeaderValue,
            didChangeLevels: false,
            isInvalid: true,
            validationMessage: 'Dimension must have at least two levels',
          });
          newSchemaDimensions.push(dimension);
          continue;
        }

        const missingLevels = levelsInPossibleDimension.filter(
          (level) =>
            !dimension.levels.map((l) => l.columnValue).includes(level),
        );
        if (missingLevels.length > 0) {
          // Take the current levels and the missing levels and produce a valid dimension,
          // so we don't fail if we encounter new values after a transform or feature generation.
          const missingLevelDefinitions: LevelDefinition[] =
            generateDimensionLevels(missingLevels);
          const upgradedDimension: DimensionDefinition = {
            ...dimension,
            levels: [...dimension.levels, ...missingLevelDefinitions],
          };
          newSchemaDimensions.push(upgradedDimension);
        } else {
          newSchemaDimensions.push(dimension);
        }
      } else {
        insights.push({
          columnHeaderValue: dimension.columnHeaderValue,
          didChangeLevels: false,
          isInvalid: true,
          validationMessage: 'Dimension not found',
        });
        newSchemaDimensions.push(dimension);
      }
    }
  }

  const newExplanationsValidation =
    currentSchema.showKeyFactors || currentSchema.showNewExplanations;

  for (const focusArea of currentSchema.focusAreas) {
    const invalidMetrics: string[] = [];

    const preferredMetrics = focusArea.metrics.filter((m) => m.isPreferred);
    if (preferredMetrics.length > 1) {
      focusAreaErrors.push({
        focusArea: focusArea.focusArea,
        invalidMetrics: focusArea.metrics.map((m) => m.columnHeaderValue),
        invalidControlVariables: [],
        validationMessage: 'Only one metric can be preferred',
      });
      newSchemaFocusAreas.push(focusArea);
      continue;
    }

    for (const metric of focusArea.metrics) {
      const validationResult = validateDatabaseSchemaMetricDefinition({
        definition: metric,
        possibleMetrics,
      });

      if (validationResult.resultType === 'err') {
        invalidMetrics.push(metric.columnHeaderValue);
      }

      if (newExplanationsValidation) {
        if (!metric.description || metric.description.trim() === '') {
          miscErrors.push({
            reason: 'new-explanations-missing-required-configuration',
            validationMessage: `The ${metric.displayName} metric inside the ${focusArea.displayName ?? focusArea.focusArea} focus area must have a description set before enabling showNewExplanations or showKeyFactors`,
          });
        }

        if (!metric.calibrationFraming) {
          miscErrors.push({
            reason: 'new-explanations-missing-required-configuration',
            validationMessage: `The ${metric.displayName} metric inside the ${focusArea.displayName ?? focusArea.focusArea} focus area must have set calibration framing before enabling showNewExplanations or showKeyFactors`,
          });
        }
      }
    }

    const invalidControlVariables = focusArea.controlVariables
      .filter((controlVariable) => {
        const isImpossible = !possibleControlVariables.includes(
          controlVariable.columnHeaderValue,
        );

        if (isImpossible) {
          return controlVariable;
        }

        if (
          newExplanationsValidation &&
          (!controlVariable.description ||
            controlVariable.description.trim() === '')
        ) {
          miscErrors.push({
            reason: 'new-explanations-missing-required-configuration',
            validationMessage: `The ${controlVariable.displayName} control variable inside the ${focusArea.displayName ?? focusArea.focusArea} focus area must have a description set before enabling showNewExplanations or showKeyFactors`,
          });
        }
      })
      .map((controlVariable) => controlVariable.columnHeaderValue);

    if (invalidMetrics.length > 0 || invalidControlVariables.length > 0) {
      focusAreaErrors.push({
        focusArea: focusArea.focusArea,
        invalidMetrics,
        invalidControlVariables,
        validationMessage: 'Invalid metrics or control variables',
      });
    }

    newSchemaFocusAreas.push(focusArea);
  }

  for (const copilot of currentSchema.copilots ?? []) {
    if (
      copilot.copilotType === 'pay-benchmarking' &&
      !copilot.externalPayBenchmarkingReference
    ) {
      copilotErrors.push({
        copilotType: 'pay-benchmarking',
        reason: 'requires-external-dataset',
      });
    }

    if (
      copilot.copilotType === 'pay-correction' &&
      !copilot.internalPayBenchmarkingReference
    ) {
      copilotErrors.push({
        copilotType: 'pay-correction',
        reason: 'requires-internal-dataset',
      });
    }

    // Check for required focus areas based on copilot type
    if (copilot.copilotType === 'hiring') {
      const hasHiringFocusArea = currentSchema.focusAreas.some(
        (fa) => fa.focusArea === 'Hiring',
      );
      const hasPerformanceFocusArea = currentSchema.focusAreas.some(
        (fa) => fa.focusArea === 'Performance',
      );

      if (!hasHiringFocusArea || !hasPerformanceFocusArea) {
        copilotErrors.push({
          copilotType: 'hiring',
          reason: 'requires-focus-areas',
          validationMessage: 'Hiring and Performance focus areas are required',
        });
      }
    }

    if (copilot.copilotType === 'promotion') {
      const hasPromotionFocusArea = currentSchema.focusAreas.some(
        (fa) => fa.focusArea === 'Promotion',
      );
      const hasPerformanceFocusArea = currentSchema.focusAreas.some(
        (fa) => fa.focusArea === 'Performance',
      );

      if (!hasPromotionFocusArea || !hasPerformanceFocusArea) {
        copilotErrors.push({
          copilotType: 'promotion',
          reason: 'requires-focus-areas',
          validationMessage:
            'Promotion and Performance focus areas are required',
        });
      }
    }

    newSchemaCopilots.push(copilot);
  }

  if (
    !isValidOverrideUploadSnapshotDate(currentSchema.overrideUploadSnapshotDate)
  ) {
    miscErrors.push({
      reason: 'invalid-override-upload-snapshot-date',
    });
  }

  const byMetricTrendsConfiguration =
    currentSchema.byMetricTrendsConfiguration ?? { isEnabled: false };

  const byMetricTrendsErrors: DatabaseRevisionSchemaValidationByMetricTrendsError[] =
    [];

  const newSchemaMetricDefinitions = newSchemaFocusAreas.flatMap(
    (focusArea) => focusArea.metrics,
  );

  const metricNames = newSchemaMetricDefinitions.map(
    (metric) => metric.columnHeaderValue,
  );
  if (byMetricTrendsConfiguration.isEnabled) {
    for (const metricConfig of byMetricTrendsConfiguration.metrics) {
      if (!possibleMetrics.includes(metricConfig.metric)) {
        byMetricTrendsErrors.push({
          type: 'metric',
          metric: metricConfig.metric,
          reason: 'Metric not found in possible metrics',
        });
      } else if (!metricNames.includes(metricConfig.metric)) {
        byMetricTrendsErrors.push({
          type: 'metric',
          metric: metricConfig.metric,
          reason: 'Metric not configured currently in any focus area',
        });
      }

      for (const availableGroupBy of metricConfig.availableGroupBys) {
        if (
          !byMetricTrendsConfiguration.groupByConfigs.some(
            (groupBy) => groupBy.key === availableGroupBy,
          )
        ) {
          byMetricTrendsErrors.push({
            type: 'metric',
            metric: metricConfig.metric,
            reason: `Group by ${availableGroupBy} is not found in group by configs`,
          });
        }
      }
    }

    for (const groupByConfig of byMetricTrendsConfiguration.groupByConfigs) {
      if (!possibleDimensionColumnHeaders.includes(groupByConfig.key)) {
        byMetricTrendsErrors.push({
          type: 'groupBy',
          key: groupByConfig.key,
          reason: 'Group by not found in possible dimensions',
        });
      }
    }
  }

  return {
    dimensions: newSchemaDimensions,
    filterSets,
    focusAreas: newSchemaFocusAreas,
    copilots: newSchemaCopilots,
    upsells: currentSchema.upsells ?? [],
    calibrationFraming:
      currentSchema.calibrationFraming ?? DefaultCalibrationFraming,
    showAllInsights: currentSchema.showAllInsights ?? false,
    showAIExplanations: currentSchema.showAIExplanations ?? false,
    showKeyFactors: currentSchema.showKeyFactors ?? false,
    showGeolocation: currentSchema.showGeolocation ?? false,
    showNewDataIntelligenceDeepDivePage:
      currentSchema.showNewDataIntelligenceDeepDivePage ?? false,
    showProgressPage: currentSchema.showProgressPage ?? false,
    showSavedAnalysesPage: currentSchema.showSavedAnalysesPage ?? false,
    showRubricsPage: currentSchema.showRubricsPage ?? false,
    showDownloadMultiAnalysisButton:
      currentSchema.showDownloadMultiAnalysisButton ?? false,
    showDownloadSingleAnalysisButton:
      currentSchema.showDownloadSingleAnalysisButton ?? false,
    showDynamicPipelineAnalysisPage:
      currentSchema.showDynamicPipelineAnalysisPage ?? false,
    showDashboardsPage: currentSchema.showDashboardsPage ?? false,
    showHeatmapAverages: currentSchema.showHeatmapAverages ?? false,
    showCustomChartTypes: currentSchema.showCustomChartTypes ?? false,
    showFobButtonInDashboardPage:
      currentSchema.showFobButtonInDashboardPage ?? false,
    showExplorePage: currentSchema.showExplorePage ?? false,
    useDisparityOnRay: currentSchema.useDisparityOnRay ?? false,
    useDisparityOnParquet: currentSchema.useDisparityOnParquet ?? false,
    useHeatmapOnRay: currentSchema.useHeatmapOnRay ?? false,
    useFAOnRay: currentSchema.useFAOnRay ?? false,
    sendHiringNotificationSummaryEmailAfterTopLevelAnalysis:
      currentSchema.sendHiringNotificationSummaryEmailAfterTopLevelAnalysis ??
      false,
    hiringNotificationEmailSubjectSuffixTemplate:
      currentSchema.hiringNotificationEmailSubjectSuffixTemplate ??
      HiringNotificationEmailSubjectSuffixTemplate.Values.NoSuffix,
    forceHiringNotificationSummaryToOnlySendToThisEmail:
      currentSchema.forceHiringNotificationSummaryToOnlySendToThisEmail,
    numPreviousDaysOfApplicantsForHiringNotificationSummaryEmail:
      currentSchema.numPreviousDaysOfApplicantsForHiringNotificationSummaryEmail,
    minReadinessRatingForHiringNotificationSummaryEmail:
      currentSchema.minReadinessRatingForHiringNotificationSummaryEmail,
    additionalEmailsToSendAllHiringNotificationSummaryEmailsTo:
      currentSchema.additionalEmailsToSendAllHiringNotificationSummaryEmailsTo,
    enableAtsWriteBack: currentSchema.enableAtsWriteBack ?? false,
    hideEmployeeLifecyclePage: currentSchema.hideEmployeeLifecyclePage ?? false,
    hideFocusAreaSolutionsPage:
      currentSchema.hideFocusAreaSolutionsPage ?? false,
    overrideUploadSnapshotDate: currentSchema.overrideUploadSnapshotDate ?? '',
    translationPhrasingCode: currentSchema.translationPhrasingCode ?? '',
    dimensionErrors: insights,
    focusAreaErrors,
    copilotErrors,
    copilotAutoMLConfigurationErrors,
    filterErrors,
    upsellErrors,
    miscErrors,
    groupByTrendsErrors: byMetricTrendsErrors,
    hasErrors:
      insights.length > 0 ||
      focusAreaErrors.length > 0 ||
      filterErrors.length > 0 ||
      copilotErrors.length > 0 ||
      upsellErrors.length > 0 ||
      miscErrors.length > 0 ||
      byMetricTrendsErrors.length > 0 ||
      copilotAutoMLConfigurationErrors.length > 0,
    hasWarnings:
      insights.length > 0 ||
      focusAreaErrors.length > 0 ||
      filterErrors.length > 0 ||
      copilotErrors.length > 0 ||
      copilotAutoMLConfigurationErrors.length > 0 ||
      upsellErrors.length > 0,
    pipelineAnalysisConfiguration: currentSchema.pipelineAnalysisConfiguration,
    byMetricTrendsConfiguration: currentSchema.byMetricTrendsConfiguration ?? {
      isEnabled: false,
    },
    copilotAutoMLConfiguration: currentSchema.copilotAutoMLConfiguration ?? {
      preset: 'no_automl',
    },
    showNewExplanations: currentSchema.showNewExplanations ?? false,
    enablePublicDashboards: currentSchema.enablePublicDashboards ?? false,
    showDashboardAISummaries: currentSchema.showDashboardAISummaries ?? false,
    showChatbot: currentSchema.showChatbot ?? false,
  };
};

export const uniqueDataSetReferences = (
  dataSetReferences: DataSetReference[],
): DataSetReference[] => {
  const hashLookup = new Set<string>();

  const newReferences: DataSetReference[] = [];

  for (const ref of dataSetReferences) {
    const key = `${ref.dataSetId}_${ref.dataSetVersion}`;

    if (hashLookup.has(key)) {
      continue;
    }

    newReferences.push(ref);
    hashLookup.add(key);
  }

  return newReferences;
};
